<script setup>
import SaveServerAction from '@frontJs/components/actions/projectManagement/dev/SaveServerAction.vue';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { userSessionKey } = defineProps({
    userSessionKey: {
        type: String,
        required: false,
        default: null,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
    globalData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: route('front.projectManagement.dev.servers', {
                        tab: props.row.original.name
                            .toLowerCase()
                            .replace(/\s/g, ''),
                    }),
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'serverName',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom du<br/>Serveur',
                }),
            cell: (props) =>
                props.row.original.serverName
                    ? h(TextCell, {
                          text: props.row.original.serverName,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'renewalDate',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Date de<br/>Renouvellement',
                }),
            cell: (props) =>
                props.row.original.serverName
                    ? h(TextCell, {
                          text: props.row.original.renewalDate,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'costExcludedTaxPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût serveur<br/>HT/mois',
                }),
            cell: (props) =>
                props.row.original.costExcludedTaxPerMonth
                    ? h(PriceCell, {
                          price: props.row.original.costExcludedTaxPerMonth,
                      })
                    : null,
            footer: () =>
                h(PriceCell, {
                    price: globalData.value.total.serverCostExcludedTaxPerMonth,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'ipBlockCostExcludedTaxPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût bloc IP<br/>HT/mois',
                }),
            cell: (props) =>
                props.row.original.ipBlockCostExcludedTaxPerMonth
                    ? h(PriceCell, {
                          price: props.row.original
                              .ipBlockCostExcludedTaxPerMonth,
                      })
                    : null,
            footer: () =>
                h(PriceCell, {
                    price: globalData.value.total
                        .ipBlockCostExcludedTaxPerMonth,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'maintenanceCostExcludedTaxPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût maintenance<br/>HT/mois',
                }),
            cell: (props) =>
                props.row.original.maintenanceCostExcludedTaxPerMonth
                    ? h(PriceCell, {
                          price: props.row.original
                              .maintenanceCostExcludedTaxPerMonth,
                      })
                    : null,
            footer: () =>
                h(PriceCell, {
                    price: globalData.value.total
                        .maintenanceCostExcludedTaxPerMonth,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'totalCloudflareCostExcludedTaxPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût Cloudflare<br/>HT/mois',
                }),
            cell: (props) =>
                props.row.original.totalCloudflareCostExcludedTaxPerMonth
                    ? h(PriceCell, {
                          price: props.row.original
                              .totalCloudflareCostExcludedTaxPerMonth,
                      })
                    : null,
            footer: () =>
                h(PriceCell, {
                    price: globalData.value.total
                        .totalCloudflareCostExcludedTaxPerMonth,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'totalCostExcludedTaxPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût total<br/>HT/mois',
                }),
            cell: (props) =>
                props.row.original.totalCostExcludedTaxPerMonth
                    ? h(PriceCell, {
                          price: props.row.original
                              .totalCostExcludedTaxPerMonth,
                      })
                    : null,
            footer: () =>
                h(PriceCell, {
                    price: globalData.value.total.totalCostExcludedTaxPerMonth,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'totalPricePerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Encaissé<br/>HT/mois',
                }),
            cell: (props) =>
                props.row.original.totalPricePerMonth
                    ? h(PriceCell, {
                          price: props.row.original.totalPricePerMonth,
                      })
                    : null,
            footer: () =>
                h(PriceCell, {
                    price: globalData.value.total.totalPricePerMonth,
                }),
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(
                                SaveServerAction,
                                {
                                    server: props.row.original,
                                    callback: updateData,
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            title: 'Modifier',
                                            icon: PencilSimpleIcon,
                                            type: 'primary',
                                        }),
                                },
                            ),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.dev.postServers'),
    columnPinning: {
        right: ['actions'],
    },
    userSessionKey: userSessionKey,
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
        with-footer
    />
</template>
